import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Bot,
  CheckCircle2,
  FlaskConical,
  MessageSquare,
  Rocket,
  Users,
  Sparkles,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type EphSummary = {
  eph_id: string
  status: string
  label?: string
  preview_url?: string
  mode?: string
  humans?: string[]
  agents?: string[]
  checks_passed?: number
  checks_total?: number
  signed_off?: boolean
  activity_count?: number
}

type EphDetail = {
  eph_id: string
  status: string
  label: string
  preview_url?: string
  mode?: string
  purpose?: string
  cloud?: string
  collaborate?: { humans?: string[]; agents?: string[]; note?: string }
  checklist?: Array<{
    id: string
    label: string
    done?: boolean
    by?: string | null
  }>
  activity?: Array<{
    at: string
    kind: string
    actor: string
    role: string
    text: string
  }>
  agent_builds?: Array<{
    at: string
    agent: string
    summary: string
    tests_passed: number
    tests_failed: number
  }>
  signoff?: { approved?: boolean; by?: string; notes?: string } | null
  sdlc_run_id?: string
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

export function EphemeralPage() {
  const { user, loading } = useAuth()
  const [envs, setEnvs] = useState<EphSummary[]>([])
  const [active, setActive] = useState<EphDetail | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [chat, setChat] = useState('')
  const [blurb, setBlurb] = useState(
    'AI agents build a disposable system; human engineers join, test, and sign off — together.',
  )

  async function refresh() {
    const snap = await api<{
      environments: EphSummary[]
      experience?: { blurb?: string }
    }>('/api/afos360/v1/ephemeral')
    setEnvs(snap.environments)
    if (snap.experience?.blurb) setBlurb(snap.experience.blurb)
  }

  async function openEnv(id: string) {
    const detail = await api<EphDetail>(
      `/api/afos360/v1/ephemeral/${encodeURIComponent(id)}`,
    )
    setActive(detail)
  }

  useEffect(() => {
    if (user?.customer_id && user?.project_id) {
      void refresh().catch((err) => setError(err instanceof Error ? err.message : 'Load failed'))
    }
  }, [user?.customer_id, user?.project_id])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function onCreate() {
    setBusy(true)
    setError(null)
    try {
      const eph = await api<EphDetail>('/api/afos360/v1/ephemeral', {
        method: 'POST',
        body: JSON.stringify({
          label: 'Human + agent co-build preview',
          purpose: 'feature_or_bugfix_verify',
        }),
      })
      setMessage(`Ephemeral ${eph.eph_id} ready — join and verify with your agents`)
      setActive(eph)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setBusy(false)
    }
  }

  async function onJoin() {
    if (!active) return
    setBusy(true)
    try {
      const eph = await api<EphDetail>(
        `/api/afos360/v1/ephemeral/${encodeURIComponent(active.eph_id)}/join`,
        { method: 'POST', body: JSON.stringify({ role: 'human' }) },
      )
      setActive(eph)
      setMessage('You joined the co-build workspace')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Join failed')
    } finally {
      setBusy(false)
    }
  }

  async function onChecklist(itemId: string, done: boolean) {
    if (!active) return
    setBusy(true)
    try {
      const eph = await api<EphDetail>(
        `/api/afos360/v1/ephemeral/${encodeURIComponent(active.eph_id)}/checklist`,
        {
          method: 'POST',
          body: JSON.stringify({ item_id: itemId, done }),
        },
      )
      setActive(eph)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checklist failed')
    } finally {
      setBusy(false)
    }
  }

  async function onChat(e: FormEvent) {
    e.preventDefault()
    if (!active || !chat.trim()) return
    setBusy(true)
    try {
      const eph = await api<EphDetail>(
        `/api/afos360/v1/ephemeral/${encodeURIComponent(active.eph_id)}/message`,
        {
          method: 'POST',
          body: JSON.stringify({ text: chat.trim(), role: 'human' }),
        },
      )
      setActive(eph)
      setChat('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Message failed')
    } finally {
      setBusy(false)
    }
  }

  async function onAgentBuild() {
    if (!active) return
    setBusy(true)
    try {
      const eph = await api<EphDetail>(
        `/api/afos360/v1/ephemeral/${encodeURIComponent(active.eph_id)}/agent-build`,
        {
          method: 'POST',
          body: JSON.stringify({
            agent: 'builder',
            summary: 'Agent pushed an incremental fix for human review',
            tests_passed: 14,
            tests_failed: 0,
          }),
        },
      )
      setActive(eph)
      setMessage('Builder agent pushed a new build to the preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Agent build failed')
    } finally {
      setBusy(false)
    }
  }

  async function onSignoff(approve: boolean) {
    if (!active) return
    setBusy(true)
    try {
      const eph = await api<EphDetail>(
        `/api/afos360/v1/ephemeral/${encodeURIComponent(active.eph_id)}/signoff`,
        {
          method: 'POST',
          body: JSON.stringify({
            approve,
            notes: approve ? 'POC looks good — ready for APPROVE_MERGE' : 'Need another pass',
          }),
        },
      )
      setActive(eph)
      setMessage(
        approve
          ? 'Signed off — discuss merge on the Tickets SDLC run'
          : 'Changes requested — agents will push another build',
      )
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-off failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-5">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-5"
        >
          <h1 className="afos-page-title flex flex-wrap items-center gap-2">
            <Sparkles className="h-7 w-7 text-[var(--color-amber)]" />
            Ephemeral co-build
          </h1>
          <p className="afos-page-lead">{blurb}</p>
          {message && <p className="mt-3 text-sm text-[var(--color-cyan)]">{message}</p>}
          {error && <p className="mt-3 text-sm text-[var(--color-rose)]">{error}</p>}
        </motion.section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-5 lg:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-[family-name:var(--font-display)] text-xl font-bold">
              Workspaces
            </h2>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onCreate()}
              className="rounded-full bg-[var(--color-cyan)] px-3 py-1.5 text-xs font-semibold text-[var(--color-void)]"
            >
              New ephemeral
            </button>
          </div>
          <div className="space-y-2">
            {envs.map((env) => (
              <button
                key={env.eph_id}
                type="button"
                onClick={() => void openEnv(env.eph_id)}
                className="w-full rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-3 text-left text-sm"
              >
                <div className="font-semibold">{env.label || env.eph_id}</div>
                <div className="mt-1 text-[11px] text-[var(--color-mist)]">
                  {env.status}
                  {env.signed_off ? ' · signed off' : ''}
                  {' · '}
                  {(env.humans || []).length} humans / {(env.agents || []).length} agents
                </div>
              </button>
            ))}
            {!envs.length && (
              <p className="text-sm text-[var(--color-mist)]">
                No previews yet. Create one, or pull a ticket SDLC run — deploy_poc mints an{' '}
                <code className="text-[var(--color-cyan)]">EPH_</code> automatically.
              </p>
            )}
          </div>
          <Link to="/tickets" className="inline-flex text-sm text-[var(--color-cyan)]">
            Open Tickets → SDLC
          </Link>
        </section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-5 sm:p-6 lg:col-span-3">
          {active ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-[family-name:var(--font-display)] text-xl font-bold">
                    {active.label}
                  </h2>
                  <p className="mt-1 font-[family-name:var(--font-mono)] text-xs text-[var(--color-mist)]">
                    {active.eph_id} · {active.status} · {active.cloud}
                  </p>
                  <p className="mt-2">
                    <span
                      className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                        active.mode === 'live_signed'
                          ? 'bg-[rgba(46,230,197,0.16)] text-[var(--color-cyan)]'
                          : 'bg-[rgba(255,179,71,0.16)] text-[var(--color-amber)]'
                      }`}
                    >
                      {active.mode === 'live_signed' ? 'live_signed' : active.mode || 'dry_run'}
                    </span>
                  </p>
                  {active.preview_url && (
                    <a
                      href={active.preview_url}
                      className="mt-2 inline-flex items-center gap-1 break-all text-sm text-[var(--color-foam)] underline-offset-2 hover:underline"
                    >
                      <Rocket className="h-3.5 w-3.5" />
                      {active.preview_url}
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onJoin()}
                  className="inline-flex items-center gap-1 rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs font-semibold"
                >
                  <Users className="h-3.5 w-3.5" />
                  Join as human
                </button>
              </div>

              <p className="text-sm text-[var(--color-mist)]">
                {active.collaborate?.note}
              </p>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-[var(--color-line)] bg-black/30 p-3">
                  <div className="mb-2 flex items-center gap-1 text-xs uppercase tracking-wider text-[var(--color-mist)]">
                    <Users className="h-3.5 w-3.5" /> Humans
                  </div>
                  <ul className="space-y-1 text-sm">
                    {(active.collaborate?.humans || []).map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-[var(--color-line)] bg-black/30 p-3">
                  <div className="mb-2 flex items-center gap-1 text-xs uppercase tracking-wider text-[var(--color-mist)]">
                    <Bot className="h-3.5 w-3.5" /> Agents
                  </div>
                  <ul className="space-y-1 text-sm">
                    {(active.collaborate?.agents || []).map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--color-mist)]">
                  <FlaskConical className="h-3.5 w-3.5" />
                  Verification checklist
                </div>
                <ul className="space-y-2">
                  {(active.checklist || []).map((item) => (
                    <li key={item.id}>
                      <label className="flex cursor-pointer items-start gap-2 rounded-2xl border border-[var(--color-line)] px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(item.done)}
                          disabled={busy}
                          onChange={(e) => void onChecklist(item.id, e.target.checked)}
                          className="mt-1"
                        />
                        <span>
                          {item.label}
                          {item.by ? (
                            <span className="block text-[11px] text-[var(--color-mist)]">
                              by {item.by}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="mb-2 text-xs uppercase tracking-wider text-[var(--color-mist)]">
                  Agent builds
                </div>
                <ul className="space-y-2 text-sm">
                  {(active.agent_builds || []).map((b, i) => (
                    <li
                      key={`${b.at}-${i}`}
                      className="rounded-2xl border border-[var(--color-line)] px-3 py-2"
                    >
                      <strong className="text-[var(--color-cyan)]">{b.agent}</strong> — {b.summary}
                      <div className="text-[11px] text-[var(--color-mist)]">
                        tests {b.tests_passed} pass / {b.tests_failed} fail · {b.at}
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onAgentBuild()}
                  className="mt-2 inline-flex items-center gap-1 rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs font-semibold"
                >
                  <Bot className="h-3.5 w-3.5" />
                  Simulate agent push
                </button>
              </div>

              <div>
                <div className="mb-2 flex items-center gap-1 text-xs uppercase tracking-wider text-[var(--color-mist)]">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Activity
                </div>
                <div className="max-h-56 space-y-2 overflow-y-auto rounded-2xl border border-[var(--color-line)] bg-black/30 p-3 text-sm">
                  {[...(active.activity || [])].reverse().map((a, i) => (
                    <div key={`${a.at}-${i}`}>
                      <span
                        className={
                          a.role === 'agent'
                            ? 'text-[var(--color-amber)]'
                            : 'text-[var(--color-cyan)]'
                        }
                      >
                        {a.actor}
                      </span>{' '}
                      <span className="text-[var(--color-mist)]">({a.kind})</span> — {a.text}
                    </div>
                  ))}
                </div>
                <form className="mt-2 flex gap-2" onSubmit={onChat}>
                  <input
                    className="min-w-0 flex-1 rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2 text-sm"
                    placeholder="Message the co-build room…"
                    value={chat}
                    onChange={(e) => setChat(e.target.value)}
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-full bg-[var(--color-foam)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
                  >
                    Send
                  </button>
                </form>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onSignoff(true)}
                  className="inline-flex items-center gap-1 rounded-full bg-[var(--color-cyan)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Sign off POC
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onSignoff(false)}
                  className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold"
                >
                  Request agent changes
                </button>
              </div>
              {active.signoff?.approved && (
                <p className="text-sm text-[var(--color-foam)]">
                  Signed off by {active.signoff.by}. Next: APPROVE_MERGE on the SDLC run
                  {active.sdlc_run_id ? ` (${active.sdlc_run_id})` : ''}.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-[var(--color-mist)]">
              Select or create an ephemeral workspace to build with your AI teammates.
            </p>
          )}
        </section>
      </main>
    </AppChrome>
  )
}
